import React, { useState, useRef, useEffect } from 'react';
import { FiX, FiArrowLeft, FiArrowRight, FiStar } from 'react-icons/fi';

const API = process.env.REACT_APP_BACKEND_URL;

const VILLA_LABEL = {
  '3bhk-garden-escape-lonavala': 'Artistic Villa (3BHK)',
  '4bhk-garden-retreat-lonavala': 'Reunion Villa (4BHK)',
  '5bhk-garden-delight-lonavala': 'Richie Villa (5BHK)',
  '12bhk-celebration-villa-lonavala': 'Celebration Villa (12BHK)',
};

/**
 * Polaroid scattered collage of real guest WhatsApp review screenshots.
 * Approved by client (Option A) — replaces old hardcoded text-grid testimonials.
 * Images live in /public/reviews/ — actual screenshots from verified stays.
 */

const REVIEWS = [
  { src: '/reviews/review-1.jpeg',  tape: 'top-left',    rotate: -6 },
  { src: '/reviews/review-2.jpeg',  tape: 'top-right',   rotate: 4  },
  { src: '/reviews/review-3.jpeg',  tape: 'top-center',  rotate: -3 },
  { src: '/reviews/review-4.jpeg',  tape: 'top-left',    rotate: 5  },
  { src: '/reviews/review-5.jpeg',  tape: 'top-right',   rotate: -4 },
  { src: '/reviews/review-6.jpeg',  tape: 'top-center',  rotate: 3  },
  { src: '/reviews/review-7.jpeg',  tape: 'top-left',    rotate: -5 },
  { src: '/reviews/review-8.jpeg',  tape: 'top-right',   rotate: 6  },
  { src: '/reviews/review-9.jpeg',  tape: 'top-center',  rotate: -2 },
  { src: '/reviews/review-10.jpeg', tape: 'top-left',    rotate: 4  },
  { src: '/reviews/review-11.jpeg', tape: 'top-right',   rotate: -6 },
];

const Tape = ({ position }) => {
  const baseStyle = {
    position: 'absolute',
    width: '90px',
    height: '24px',
    background: 'linear-gradient(180deg, rgba(255,235,150,0.85) 0%, rgba(255,215,90,0.55) 100%)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    mixBlendMode: 'multiply',
    pointerEvents: 'none',
  };
  if (position === 'top-left') return <div style={{ ...baseStyle, top: '-10px', left: '14px', transform: 'rotate(-18deg)' }} />;
  if (position === 'top-right') return <div style={{ ...baseStyle, top: '-10px', right: '14px', transform: 'rotate(16deg)' }} />;
  return <div style={{ ...baseStyle, top: '-12px', left: '50%', marginLeft: '-45px', transform: 'rotate(2deg)' }} />;
};

export default function GuestReviewWall() {
  const [lightbox, setLightbox] = useState(null);
  const [published, setPublished] = useState([]);
  const trackRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/reviews/published?limit=20`)
      .then(r => r.ok ? r.json() : { reviews: [] })
      .then(d => setPublished(d.reviews || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (lightbox !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightbox]);

  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowLeft') setLightbox((i) => (i > 0 ? i - 1 : REVIEWS.length - 1));
      if (e.key === 'ArrowRight') setLightbox((i) => (i < REVIEWS.length - 1 ? i + 1 : 0));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  const next = () => setLightbox((i) => (i < REVIEWS.length - 1 ? i + 1 : 0));
  const prev = () => setLightbox((i) => (i > 0 ? i - 1 : REVIEWS.length - 1));

  const scrollTrack = (dir) => {
    if (!trackRef.current) return;
    const amount = trackRef.current.clientWidth * 0.7;
    trackRef.current.scrollBy({ left: dir === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  return (
    <>
      <section
        data-testid="guest-review-wall"
        className="relative py-20 md:py-24 overflow-hidden"
        style={{
          background: `
            radial-gradient(circle at 20% 20%, rgba(245,124,32,0.06) 0%, transparent 40%),
            radial-gradient(circle at 80% 70%, rgba(27,36,50,0.04) 0%, transparent 50%),
            linear-gradient(180deg, #FAF7F2 0%, #F5EFE6 100%)
          `,
        }}
      >
        {/* Grain texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.18] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.4'/%3E%3C/svg%3E")`,
          }}
        />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="text-center mb-14">
            <p className="text-[11px] tracking-[0.3em] uppercase mb-3 font-semibold" style={{ color: '#F57C20' }}>
              · Real Words · Real Guests · Real Stays ·
            </p>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl text-[#1A1A2E] leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Pinned with <span className="italic text-[#F57C20]">Love</span>
            </h2>
            <p className="text-[#6B7280] text-sm mt-4 max-w-xl mx-auto">
              Unedited screenshots from guests who stayed at Royal Garden Villas. Tap any postcard to read the full message.
            </p>

            <div className="inline-flex items-center gap-2 mt-6 bg-white/70 backdrop-blur-sm border border-[#1A1A2E]/10 rounded-full px-4 py-2 shadow-sm">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <FiStar key={i} className="w-4 h-4 text-[#F57C20] fill-[#F57C20]" />
                ))}
              </div>
              <span className="text-[#1A1A2E] text-sm font-bold">4.9</span>
              <span className="text-[#6B7280] text-xs">· 200+ verified stays</span>
            </div>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none bg-gradient-to-r from-[#F5EFE6] to-transparent" />
            <div className="hidden md:block absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none bg-gradient-to-l from-[#F5EFE6] to-transparent" />

            <button
              onClick={() => scrollTrack('prev')}
              data-testid="review-track-prev"
              aria-label="Scroll previous"
              className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white shadow-lg items-center justify-center text-[#1A1A2E] hover:bg-[#F57C20] hover:text-white transition-colors"
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollTrack('next')}
              data-testid="review-track-next"
              aria-label="Scroll next"
              className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white shadow-lg items-center justify-center text-[#1A1A2E] hover:bg-[#F57C20] hover:text-white transition-colors"
            >
              <FiArrowRight className="w-5 h-5" />
            </button>

            <div
              ref={trackRef}
              className="flex gap-8 md:gap-12 overflow-x-auto pb-16 pt-8 px-4 md:px-12 snap-x snap-mandatory review-wall-track"
            >
              {published.map((r, i) => {
                const rotate = (i % 2 === 0 ? -5 : 5) + (i % 3);
                return (
                  <div
                    key={`pub-${r.id}`}
                    data-testid={`published-review-${i}`}
                    className="snap-center flex-shrink-0"
                    style={{
                      transform: `rotate(${rotate}deg)`,
                      transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = `rotate(0deg) translateY(-12px) scale(1.04)`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = `rotate(${rotate}deg)`; }}
                  >
                    <div
                      className="relative bg-white p-4 pb-10 shadow-xl flex flex-col"
                      style={{
                        width: 'clamp(220px, 60vw, 280px)',
                        minHeight: '360px',
                        boxShadow: '0 18px 40px -12px rgba(26,26,46,0.35), 0 4px 12px rgba(26,26,46,0.12)',
                      }}
                    >
                      <Tape position={i % 3 === 0 ? 'top-left' : i % 3 === 1 ? 'top-right' : 'top-center'} />
                      <div className="flex justify-center gap-0.5 mt-2 mb-3">
                        {[...Array(5)].map((_, s) => (
                          <FiStar key={s} className="w-4 h-4 text-[#F57C20] fill-[#F57C20]" />
                        ))}
                      </div>
                      <p className="text-[#1A1A2E] text-sm leading-snug px-1 flex-grow" style={{ fontFamily: "'Caveat', 'Brush Script MT', cursive", fontSize: '20px', lineHeight: '1.35' }}>
                        "{r.remark_text}"
                      </p>
                      <div className="absolute bottom-2 left-0 right-0 text-center px-2">
                        <p className="text-[#1A1A2E] text-sm" style={{ fontFamily: "'Caveat', 'Brush Script MT', cursive" }}>
                          — {r.guest_name || 'Verified Guest'}
                        </p>
                        {VILLA_LABEL[r.villa_id] && (
                          <p className="text-[#6B7280] text-[10px] mt-0.5">{VILLA_LABEL[r.villa_id]}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {REVIEWS.map((r, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(i)}
                  data-testid={`review-polaroid-${i}`}
                  aria-label={`Open guest review ${i + 1}`}
                  className="snap-center flex-shrink-0 group focus:outline-none"
                  style={{
                    transform: `rotate(${r.rotate}deg)`,
                    transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = `rotate(0deg) translateY(-12px) scale(1.04)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = `rotate(${r.rotate}deg)`; }}
                >
                  <div
                    className="relative bg-white p-3 pb-12 shadow-xl"
                    style={{
                      width: 'clamp(220px, 60vw, 280px)',
                      boxShadow: '0 18px 40px -12px rgba(26,26,46,0.35), 0 4px 12px rgba(26,26,46,0.12)',
                    }}
                  >
                    <Tape position={r.tape} />
                    <div className="relative overflow-hidden bg-[#F7F8FA]" style={{ aspectRatio: '3/4' }}>
                      <img
                        src={r.src}
                        alt={`Royal Garden Villas guest WhatsApp review ${i + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: 'linear-gradient(180deg, rgba(255,240,210,0.04) 0%, rgba(120,80,30,0.06) 100%)',
                          mixBlendMode: 'multiply',
                        }}
                      />
                    </div>
                    <div className="absolute bottom-2 left-0 right-0 text-center">
                      <p className="text-[#1A1A2E] text-sm" style={{ fontFamily: "'Caveat', 'Brush Script MT', cursive" }}>
                        Verified Guest
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <p className="text-center text-[#9CA3AF] text-xs mt-2">
            ← Drag or swipe · Tap any card to enlarge →
          </p>

          {/* SHARE YOUR STAY CTA — drives organic UGC reviews via WhatsApp */}
          <div className="mt-12 flex justify-center">
            <a
              href="https://wa.me/919820088292?text=Hi%20Royal%20Garden%20Villas%2C%20I%20stayed%20with%20you%20and%20would%20love%20to%20share%20my%20review%20%F0%9F%92%9B"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-your-stay-cta"
              className="group inline-flex items-center gap-3 bg-white border border-[#1A1A2E]/10 hover:border-[#25D366] hover:bg-[#25D366]/5 rounded-full pl-2 pr-6 py-2 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#25D366] text-white shadow group-hover:scale-105 transition-transform">
                {/* WhatsApp glyph */}
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                  <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.566 5.339l-.999 3.648 3.922-.286z"/>
                </svg>
              </span>
              <span className="text-left">
                <span className="block text-[#1A1A2E] text-sm font-semibold leading-tight">
                  Stayed with us? Share your story
                </span>
                <span className="block text-[#6B7280] text-[11px] leading-tight">
                  One tap · WhatsApp us your review screenshot
                </span>
              </span>
            </a>
          </div>
        </div>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');
          .review-wall-track { scrollbar-width: none; }
          .review-wall-track::-webkit-scrollbar { display: none; }
        `}</style>
      </section>

      {lightbox !== null && (
        <div
          data-testid="review-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: 'rgba(10,10,15,0.92)', backdropFilter: 'blur(8px)' }}
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            data-testid="review-lightbox-close"
            aria-label="Close review"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="review-lightbox-prev"
            aria-label="Previous review"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <FiArrowLeft className="w-6 h-6" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="review-lightbox-next"
            aria-label="Next review"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <FiArrowRight className="w-6 h-6" />
          </button>

          <img
            src={REVIEWS[lightbox].src}
            alt={`Guest review ${lightbox + 1}`}
            className="max-w-full max-h-[88vh] object-contain shadow-2xl rounded-sm"
            onClick={(e) => e.stopPropagation()}
          />

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-widest uppercase">
            {lightbox + 1} / {REVIEWS.length}
          </div>
        </div>
      )}
    </>
  );
}
